/**
 * Alle Oberflächentexte, deutsch (de-DE).
 *
 * Nach Entscheidung E-F-12 ist das erste Release deutsch, aber vollständig
 * übersetzbar gebaut: In Komponenten steht kein Anzeigetext. Eine zweite Sprache
 * ist damit eine weitere Datei neben dieser und ein Wechsel in `src/i18n/index.ts`
 * — kein Durchsuchen von Komponenten.
 *
 * Fachliche Aufzählungen (Vorgangsstatus, Raumzustand) stehen als stabile
 * Schlüssel im Backend und werden erst hier übersetzt. Genau dieser Punkt wäre
 * nachträglich am teuersten zu korrigieren (E-F-12).
 */

export const de = {
  app: {
    name: "BauSepp",
    fullName: "BauSepp",
    tagline: "Objekte, Vorgänge und Aufgaben vor Ort erfassen",
  },

  common: {
    save: "Speichern",
    edit: "Bearbeiten",
    cancel: "Abbrechen",
    close: "Schließen",
    back: "Zurück",
    retry: "Erneut versuchen",
    discard: "Verwerfen",
    search: "Suchen",
    searchPlaceholder: "Nach Objekt, Raum oder Nummer suchen",
    loading: "Wird geladen …",
    more: "Mehr",
    of: "von",
    /**
     * Satzklammer um die Zahl der gezeigten Einträge, wenn der Server die
     * Gesamtzahl nicht mitliefert: „Es werden die ersten 100 Einträge gezeigt —
     * es gibt weitere." Zwei Schlüssel und nicht einer, weil die Zahl aus den
     * Daten kommt; dieselbe Klammer wie bei `{n} von {m}`.
     */
    firstEntriesShown: "Es werden die ersten",
    entriesShownSuffix: "Einträge gezeigt — es gibt weitere.",
    none: "—",
    optional: "optional",
    required: "Pflichtfeld",
    yes: "Ja",
    no: "Nein",
    openMenu: "Menü öffnen",
    skipToContent: "Direkt zum Inhalt",
  },

  /**
   * Ortsangaben und die Wege dorthin.
   *
   * Eigener Abschnitt, weil dieselbe Angabe an fünf Stellen steht: Vorgang,
   * Aufgabe, Termin, Aufnahme und Gewährleistung nennen alle denselben Ort. Ein
   * Ort, der je nach Ansicht anders heißt, liest sich wie zwei verschiedene.
   */
  place: {
    label: "Ort",
    property: "Objekt",
    room: "Raum",
    component: "Bauteil",
    issue: "Vorgang",
    task: "Aufgabe",
    project: "Projekt",
    openProperty: "Objekt öffnen",
    openRoom: "Raum öffnen",
    openIssue: "Vorgang öffnen",
    openTask: "Aufgabe öffnen",
    openProject: "Projekt öffnen",
    openOffer: "Angebot öffnen",
    /** Steht, wo ein Bezug erfasst ist, aber kein Name dazu geladen werden konnte. */
    unnamed: "ohne Bezeichnung",
  },

  nav: {
    /** Mobile Hauptnavigation nach Kapitel 38 — genau diese sieben. */
    mobile: {
      home: "Start",
      properties: "Objekte",
      tasks: "Aufgaben",
      report: "Meldung",
      calendar: "Kalender",
      caretaker: "Mein Tag",
      messages: "Nachrichten",
      more: "Mehr",
    },
    /** Desktop-Navigation nach Kapitel 38 — die vierzehn Bereiche. */
    desktop: {
      dashboard: "Dashboard",
      portfolio: "Portfolio",
      properties: "Objekte",
      projects: "Projekte",
      plans: "Grundrisse",
      issues: "Vorgänge",
      tasks: "Aufgaben",
      calendar: "Kalender",
      communication: "Kommunikation",
      documents: "Dokumente",
      costs: "Angebote/Kosten",
      reports: "Berichte",
      contacts: "Kontakte",
      settings: "Einstellungen",
      users: "Nutzer",
      account: "Konto",
      caretaker: "Mein Tag",
      inspections: "Rundgänge",
      media: "Fotos und Messwerte",
      warranties: "Gewährleistung",
    },
    sectionWork: "Arbeit",
    sectionData: "Bestand",
    sectionBusiness: "Kaufmännisch",
    /** Vorlesetexte für die beiden Navigationsbereiche — nicht sichtbar. */
    ariaBottom: "Hauptbereiche",
    ariaSide: "Alle Bereiche",
  },

  login: {
    title: "Anmelden",
    subtitle: "Zugang zur Objekt- und Instandhaltungsverwaltung",
    email: "E-Mail",
    password: "Passwort",
    deviceLabel: "Gerätebezeichnung",
    deviceLabelHint: "Erscheint in der Geräteübersicht, etwa „Diensttelefon Hausmeister“",
    organizationFromFirstStep:
      "Die Anmeldung gilt für die im ersten Schritt gewählte Organisation. Ein Wechsel ist nach der Anmeldung möglich.",
    organization: "Organisation",
    organizationHint: "Nur nötig, wenn Ihr Konto mehreren Organisationen zugeordnet ist",
    submit: "Anmelden",
    submitting: "Wird angemeldet …",
    mfaTitle: "Zweiter Faktor",
    mfaHint: "Bitte den sechsstelligen Code aus der Authenticator-App eingeben.",
    mfaCode: "Code",
    mfaSubmit: "Bestätigen",
    mfaBack: "Andere Zugangsdaten verwenden",
    mfaSetupRequired:
      "Für Ihre Rolle ist die Zwei-Faktor-Anmeldung Pflicht, aber noch nicht eingerichtet. Bitte in den Einstellungen nachholen.",
    failed: "Anmeldung nicht möglich",
    offlineHint:
      "Für die Anmeldung wird eine Verbindung benötigt. Bereits erfasste Meldungen bleiben gespeichert.",
  },

  dashboard: {
    title: "Start",
    greeting: "Übersicht",
    dueToday: "Heute fällig",
    overdue: "Überfällig",
    criticalIssues: "Kritische Vorgänge",
    nothingDueToday: "Heute steht nichts an",
    nothingOverdue: "Nichts überfällig",
    nothingCritical: "Keine kritischen Vorgänge",
    nothingToApprove: "Keine Freigaben ausstehend",
    openApprovals: "Offene Freigaben",
    nextAppointments: "Nächste Termine",
    missingReplies: "Fehlende Rückmeldungen",
    budgetDeviations: "Budgetabweichungen",
    newDocuments: "Neue Dokumente",
    recentChanges: "Kürzlich geändert",
    aiSuggestions: "KI-Vorschläge",
    portfolio: "Bestand",
    propertiesCount: "Objekte",
    roomsCount: "Räume",
    openIssuesCount: "Offene Vorgänge",
    quickReport: "Neue Meldung",
    quickReportHint: "Sprechen, Foto oder Text — in unter 30 Sekunden",
    noData: "Keine Einträge",
  },

  properties: {
    title: "Objekte",
    empty: "Noch kein Objekt angelegt",
    // Bis zum 30.07.2026 stand hier, Objekte würden „über die Verwaltung"
    // angelegt. Seit es die Anlegemaske gibt, ist das falsch — ein Hinweistext,
    // der auf einen Weg verweist, den es nicht mehr gibt, schickt den Nutzer
    // in die Irre.
    emptyHint: "Legen Sie das erste Objekt an — Räume, Vorgänge und Kosten hängen daran.",
    searchEmpty: "Kein Objekt passt zur Suche",
    buildings: "Gebäude",
    rooms: "Räume",
    openIssues: "Offene Vorgänge",
    openIssuesShort: "offen",
    constructionYear: "Baujahr",
    buildingType: "Gebäudeart",
    usageType: "Nutzung",
    address: "Adresse",
    facts: "Objektdaten",
    riskConfirmed: "bestätigt",
    riskSuspected: "gemeldet, nicht bestätigt",
    riskSeverity: "Einstufung",
    riskSource: "Quelle",
    structure: "Struktur",
    structureEmpty: "Für dieses Objekt ist noch keine Struktur erfasst",
    knownRisks: "Bekannte Risiken",
    emergency: "Notfall",
    technicalNotes: "Technische Hinweise",
    floorLevel: "Ebene",
    protectedRoom: "Geschützt",
    protectedRoomHint: "Bereits saniert — Arbeiten nur nach Freigabe",
    notFound: "Objekt nicht gefunden",
  },

  /**
   * Portfolio — die Ebene zwischen Organisation und Objekt (Kapitel 7).
   *
   * Die Texte benennen durchgehend die **Folge** einer Handlung statt nur ihren
   * Namen: dass ein gelöschtes Bündel seine Objekte freigibt, dass ein Objekt
   * zu höchstens einem Bündel gehört und eine neue Zuordnung die alte ersetzt.
   * Beides ist ohne Erklärung nicht ersichtlich und wäre nachträglich teuer
   * aufzuklären.
   */
  portfolio: {
    title: "Portfolio",
    subtitle: "Bündel von Objekten — für Auswertung und Zuständigkeit",
    empty: "Noch kein Portfolio angelegt",
    emptyHint:
      "Ein Portfolio fasst mehrere Objekte unter einem Dach zusammen: für Auswertungen und als Zuständigkeitsbereich. Ein Objekt kann auch ohne Portfolio bestehen.",
    searchEmpty: "Kein Portfolio passt zur Suche",
    searchPlaceholder: "Nach Bezeichnung oder Beschreibung suchen",
    notFound: "Portfolio nicht gefunden",
    loadFailed: "Die Portfolios konnten nicht geladen werden",

    properties: "Objekte",
    rooms: "Räume",
    openIssues: "Offene Vorgänge",
    propertiesEmpty: "Diesem Portfolio ist noch kein Objekt zugeordnet",
    propertiesEmptyHint:
      "Ordnen Sie vorhandene Objekte zu. Sie bleiben eigenständig — das Portfolio ist eine Sicht darauf, keine Kopie.",

    createAction: "Portfolio anlegen",
    createTitle: "Neues Portfolio anlegen",
    createDescription:
      "Ein Portfolio bündelt mehrere Objekte unter einem Dach — für Auswertungen und als Zuständigkeitsbereich.",
    createWhy:
      "Pflicht ist allein die Bezeichnung. Objekte kommen anschließend dazu und lassen sich jederzeit wieder lösen.",
    editAction: "Bearbeiten",
    editTitle: "Portfolio ändern",
    editDescription:
      "Geändert wird nur, was Sie hier anfassen — die übrigen Angaben bleiben unberührt.",
    name: "Bezeichnung",
    nameHint: "Wie das Bündel im Haus genannt wird, etwa „Wohnanlagen Volmetal“.",
    description: "Beschreibung",
    descriptionHint:
      "Wofür steht dieses Bündel, und was gehört hinein? Der Satz hilft dem Nächsten bei der Zuordnung.",
    submitCreate: "Portfolio anlegen",
    submitUpdate: "Änderungen speichern",
    created: "Portfolio angelegt",
    updated: "Portfolio geändert",
    createFailed: "Portfolio konnte nicht angelegt werden",
    updateFailed: "Portfolio konnte nicht geändert werden",
    needsName: "Bitte eine Bezeichnung angeben",
    nothingChanged: "Nichts geändert",
    nextStep: "Als Nächstes: Objekte zuordnen — ohne sie bleibt die Auswertung leer.",

    assignAction: "Objekte zuordnen",
    assignTitle: "Objekte diesem Portfolio zuordnen",
    assignDescription:
      "Mehrere auf einmal — die Zuordnung läuft als ein Vorgang, der entweder ganz oder gar nicht durchläuft.",
    assignHint:
      "Ein Objekt gehört zu höchstens einem Portfolio. Ist es bereits einem anderen zugeordnet, ersetzt diese Auswahl die bisherige — der vorherige Stand bleibt im Protokoll nachvollziehbar.",
    assignAlreadyHere: "bereits in diesem Portfolio",
    assignInOther: "derzeit in einem anderen Portfolio",
    assignSubmit: "Zuordnen",
    assignNone: "Bitte mindestens ein Objekt auswählen",
    assignDone: "Objekte zugeordnet",
    assignFailed: "Die Zuordnung ist nicht durchgelaufen",
    assignNoProperties: "Es gibt noch kein Objekt, das sich zuordnen ließe",
    assignSelected: "ausgewählt",

    unassignAction: "Aus Portfolio lösen",
    unassignConfirm:
      "Das Objekt bleibt mit allen Räumen, Vorgängen und Kosten bestehen — es ist danach nur keinem Portfolio mehr zugeordnet.",
    unassignDone: "Objekt gelöst",
    unassignFailed: "Das Objekt konnte nicht gelöst werden",

    deleteAction: "Portfolio löschen",
    deleteTitle: "Portfolio löschen",
    deleteDescription:
      "Die zugeordneten Objekte bleiben bestehen und sind danach keinem Portfolio mehr zugeordnet. Gelöscht wird ausschließlich das Bündel.",
    deleteReason: "Begründung",
    deleteReasonHint:
      "Steht im Protokoll und beantwortet später die Frage, warum ein Bündel verschwunden ist.",
    deleteSubmit: "Endgültig löschen",
    deleteDone: "Portfolio gelöscht",
    deleteFailed: "Portfolio konnte nicht gelöscht werden",
  },

  report: {
    title: "Neue Meldung",
    /** Kapitel 40: genau drei primäre Aktionen. */
    subtitle: "Wie möchten Sie erfassen?",
    speak: "Sprechen",
    speakHint: "Sachverhalt diktieren, Transkript folgt",
    photo: "Foto",
    photoHint: "Aufnehmen, wird auf dem Gerät verkleinert",
    text: "Text",
    textHint: "Kurz eintippen oder Beleg scannen",

    recordingTitle: "Aufnahme läuft",
    recordingHint: "Ruhig sprechen. Zum Beenden auf „Fertig“ tippen.",
    recordingStop: "Fertig",
    recordingCancel: "Aufnahme verwerfen",
    recordingDuration: "Dauer",

    cameraTitle: "Foto aufnehmen",
    cameraShutter: "Aufnehmen",
    cameraSwitch: "Kamera wechseln",
    cameraFallbackHint: "Die Kamera-App des Geräts wird geöffnet.",

    textTitle: "Sachverhalt beschreiben",
    textPlaceholder: "Was ist wo aufgefallen?",
    textLabel: "Beschreibung",
    /** Handoff Screen 1d, Schritt 2: Vorschau heißt „Transkript", solange der
     *  Text (auch) aus einer Aufnahme stammt — nicht „Beschreibung", das
     *  klänge nach einer getippten Angabe. */
    transcriptLabel: "Transkript",

    locationTitle: "Ort",
    locationHint: "Objekt und Raum können später ergänzt werden",
    locationProperty: "Objekt",
    locationRoom: "Raum",
    locationUnassigned: "Ohne Zuordnung",
    /** Handoff Screen 1d: Korrekturweg für den vorbelegten Ort. */
    locationChange: "ändern",
    locationDone: "fertig",
    locationChooseHint: "Wo bist Du gerade?",
    /** Nur gezeigt, wenn die Vorbelegung tatsächlich aus dem letzten
     *  Rundgang stammt — nicht bei der Vorauswahl über „einziges Objekt". */
    locationFromLastRound: "Ort aus Deinem letzten Rundgang übernommen — nicht geraten.",

    /** Handoff Screen 1d: Dublettenschutz — laufende Meldungen desselben Raums. */
    duplicatesTitle: "Zuletzt hier gemeldet",
    duplicatesHint: "Damit dasselbe Loch nicht dreimal im System landet.",

    /** Handoff Screen 1d: Zusage, dass die Erfassung auch ohne Netz gilt. */
    offlineReassurance: "Kein Netz im Keller? Erfassung ist trotzdem sofort gespeichert.",

    attachments: "Anhänge",
    attachmentPhoto: "Foto",
    attachmentAudio: "Sprachaufnahme",
    removeAttachment: "Anhang entfernen",

    submit: "Meldung speichern",
    submitHint: "Wird örtlich gespeichert und im Hintergrund übertragen",
    savedTitle: "Meldung gespeichert",
    savedBody: "Die Übertragung läuft im Hintergrund. Sie können weiterarbeiten.",
    savedAnother: "Weitere Meldung",
    savedDone: "Fertig",

    transcribe: "In Text",
    transcribeConfidence: "Verlässlichkeit",
    audioWithoutText:
      "Die Aufnahme wurde nicht in Text umgewandelt. Ohne Text ist die Meldung später nur durch Anhören auffindbar — die Umwandlung holt das nach.",
    transcribeFailed: "Die Aufnahme konnte nicht in Text umgewandelt werden",
    emergencySuspected:
      "Der Wortlaut deutet auf eine Gefahr hin. Bitte prüfen und im Zweifel sofort handeln.",
    needsRoomForAttachments:
      "Für Fotos und Aufnahmen bitte den Raum wählen — ohne Ortsangabe sind sie als Nachweis nicht verwertbar (Kapitel 14.1).",
    needsProperty: "Bitte zuerst das Objekt wählen",
    needsContent: "Bitte einen Text erfassen oder eine Aufnahme hinzufügen",
    cameraUnavailable: "Die Kamera ist auf diesem Gerät nicht erreichbar",
    micUnavailable: "Das Mikrofon ist auf diesem Gerät nicht erreichbar",
    permissionDenied: "Der Zugriff wurde abgelehnt. Bitte in den Browsereinstellungen erlauben.",

    /* Ortsangabe im Freitext, wenn kein Raum gewählt ist (Kapitel 12.2). Ohne
       Raum **oder** Ortsangabe lässt sich die Meldung nicht einreichen. */
    locationNote: "Ortsangabe",
    locationNotePlaceholder: "z. B. hinter der Bar, linke Wand",
    locationNoteHint:
      "Wenn kein Raum passt: Wo genau? Eine Meldung braucht entweder einen Raum oder diese Angabe.",
    needsLocation: "Bitte einen Raum wählen oder eine Ortsangabe eintragen",

    /* Entwurf. Die Texte sagen ausdrücklich, wo gesichert wird und was nicht
       mitgesichert wird — ein Speicher, dem man nicht ansieht, was er hält,
       ist schlimmer als keiner. */
    draftSaving: "Wird gesichert …",
    draftSavedAt: "Gesichert",
    draftUnsaved: "Noch nicht gesichert",
    draftFailed: "Sichern fehlgeschlagen — wird erneut versucht",
    draftOffline: "Ohne Verbindung wird nicht gesichert",
    draftHint:
      "Der Entwurf liegt auf dem Server und steht auch auf anderen Geräten bereit. Fotos und Aufnahmen bleiben auf diesem Gerät und gehören nicht zum gesicherten Entwurf.",
    draftResumed: "Fortgesetzter Entwurf",
    draftResumedAttachments:
      "Zu diesem Entwurf lagen Anhänge auf einem anderen Gerät. Sie sind hier nicht verfügbar.",
    draftStartNew: "Neue Meldung beginnen",
    draftDiscard: "Entwurf verwerfen",
  },

  account: {
    title: "Konto",
    mfa: "Zweiter Faktor",
    mfaActive: "Eingerichtet",
    mfaActiveHint: "Bei der Anmeldung wird zusätzlich ein Code abgefragt.",
    mfaIntro:
      "Ein zweiter Faktor schützt das Konto auch dann, wenn das Passwort bekannt wird.",
    mfaStart: "Zweiten Faktor einrichten",
    mfaSecret: "Geheimnis für die App",
    mfaCode: "Code aus der App",
    mfaConfirm: "Einrichtung abschließen",
    mfaDone: "Zweiter Faktor ist aktiv",
    mfaFailed: "Einrichtung nicht möglich",
    recoveryCodes: "Ersatzcodes",
    recoveryOnce: "Jetzt notieren — sie werden kein zweites Mal angezeigt.",
    password: "Passwort",
    currentPassword: "Bisheriges Passwort",
    newPassword: "Neues Passwort",
    repeatPassword: "Neues Passwort wiederholen",
    passwordMismatch: "Die beiden Eingaben stimmen nicht überein.",
    changePassword: "Passwort ändern",
    passwordChanged: "Passwort geändert",
    passwordFailed: "Passwort nicht geändert",
    devices: "Angemeldete Geräte",
    noSessions: "Keine weiteren Anmeldungen.",
    unknownDevice: "Unbekanntes Gerät",
    thisDevice: "Dieses Gerät",
    lastUsed: "zuletzt",
    withoutMfa: "ohne zweiten Faktor",
    revoke: "Abmelden",
    sessionRevoked: "Gerät abgemeldet",
    /**
     * Hinweis zu einem lange nicht genutzten Gerät.
     *
     * Reine Beobachtung, keine Aufforderung mit erhobenem Zeigefinger: Ob ein
     * Gerät noch im Einsatz ist, weiß nur, wer es in der Hand hatte — die
     * Anwendung kennt nur die Zeitspanne.
     */
    staleDeviceHint: (tage: number) =>
      `Seit ${tage} Tagen nicht mehr benutzt — abmelden, wenn es nicht mehr in Gebrauch ist.`,
    /*
     * Abmelden dieses Geräts. Getrennt benannt von `revoke`: Jenes meldet ein
     * **fremdes** Gerät ab, dies das eigene — in einer Liste, die beides
     * anbietet, wäre zweimal „Abmelden" die gefährlichere Beschriftung.
     */
    logout: "Von diesem Gerät abmelden",
    logoutShort: "Abmelden",
    logoutHint:
      "Beendet die Sitzung auf diesem Gerät. Gespeicherte Zugangsdaten und geladene Daten werden dabei entfernt; noch nicht abgeschickte Meldungen bleiben in der Warteschlange.",
    loggedOut: "Abgemeldet",
  },

  /**
   * Nutzerverwaltung (Kapitel 9.1 bis 9.3).
   *
   * Bis hierher gab es sie nicht: Ein neuer Hausmeister musste über die
   * Kommandozeile angelegt werden. Die Texte hier tragen deshalb mehr Erklärung
   * als anderswo — wer Rollen vergibt, entscheidet über Kostensicht und
   * Freigaben und soll nicht raten müssen.
   */
  users: {
    title: "Nutzer",
    subtitle: "Konten, Rollen und Zugänge dieser Organisation",
    searchPlaceholder: "Nach Name oder E-Mail suchen",
    empty: "Noch keine weiteren Konten",
    emptyHint: "Über „Person hinzufügen“ entsteht ein Zugang samt Einladungslink.",
    searchEmpty: "Kein Konto zu dieser Suche",

    // Liste
    self: "Ihr Konto",
    inactive: "Deaktiviert",
    invited: "Eingeladen",
    invitedHint: "Der Zugang wurde noch nicht eingerichtet.",
    neverLoggedIn: "noch nie angemeldet",
    lastLogin: "zuletzt angemeldet",
    mfaMissing: "Zweiter Faktor fehlt",
    mfaMissingHint: "Diese Rolle verlangt eine Zwei-Faktor-Anmeldung.",
    validUntil: "befristet bis",

    // Anlegen
    createAction: "Person hinzufügen",
    createTitle: "Person hinzufügen",
    createDescription:
      "Das Konto entsteht ohne Passwort. Sie erhalten einen Einladungslink, den Sie persönlich weitergeben — das Passwort vergibt die eingeladene Person selbst.",
    email: "E-Mail-Adresse",
    firstName: "Vorname",
    lastName: "Nachname",
    phone: "Telefon",
    jobTitle: "Funktion",
    jobTitleHint: "Erscheint neben dem Namen, etwa „Haustechnik“",
    role: "Rolle",
    roleRequired: "Bitte eine Rolle wählen",
    submitCreate: "Anlegen und Link erzeugen",
    createFailed: "Konto nicht angelegt",

    // Einladungslink
    invitationTitle: "Einladungslink",
    invitationOnce: "Jetzt notieren — er erscheint kein zweites Mal.",
    invitationCopy: "Link kopieren",
    invitationCopied: "Link kopiert",
    invitationExpires: "Gültig bis",
    invitationDone: "Fertig",
    reissueAction: "Zugang neu einrichten",
    reissueHint:
      "Erzeugt einen neuen Einladungslink. Das bisherige Passwort gilt, bis der Link eingelöst wird.",
    reissueFailed: "Kein neuer Link erzeugt",

    // Rolle ändern
    changeRoleTitle: "Rolle ändern",
    changeRoleAction: "Rolle ändern",
    currentRole: "Bisher",
    submitRole: "Rolle übernehmen",
    roleChanged: "Rolle geändert",
    roleFailed: "Rolle nicht geändert",
    mfaConsequence:
      "Für diese Rolle ist eine Zwei-Faktor-Anmeldung Pflicht. Ist sie noch nicht eingerichtet, wird die betroffene Person abgemeldet und beim nächsten Anmelden dorthin geführt.",

    // Deaktivieren
    deactivateAction: "Zugang deaktivieren",
    deactivateTitle: "Zugang deaktivieren",
    deactivateDescription:
      "Der Zugang erlischt sofort, das Konto bleibt bestehen. Gemeldete Vorgänge, Fotos und Abnahmen bleiben dieser Person zugeordnet — sie dürfen ihren Urheber nicht verlieren.",
    deactivateReason: "Grund fürs Protokoll",
    submitDeactivate: "Deaktivieren",
    deactivated: "Zugang deaktiviert",
    deactivateFailed: "Zugang nicht deaktiviert",
    activateAction: "Wieder freischalten",
    activated: "Zugang wieder freigeschaltet",

    // Rollenauswahl
    roleRequiresMfa: "Zwei-Faktor-Pflicht",
    rolePermissions: "Darf",
    roleNoSpecialRights: "Keine Kosten-, Freigabe- oder Verwaltungsrechte",
  },

  /** Einladung einlösen — die Seite, auf die der Einladungslink führt. */
  invitation: {
    title: "Zugang einrichten",
    checking: "Einladung wird geprüft …",
    invalid: "Dieser Einladungslink ist nicht mehr gültig",
    invalidHint:
      "Links laufen nach einer Woche ab und wirken nur einmal. Bitte einen neuen bei Ihrer Verwaltung anfordern.",
    forAccount: "Zugang für",
    atOrganization: "bei",
    password: "Neues Passwort",
    passwordHint: "Mindestens 12 Zeichen. Keine Vorgaben zu Sonderzeichen.",
    repeat: "Passwort wiederholen",
    mismatch: "Die beiden Eingaben stimmen nicht überein.",
    submit: "Passwort setzen",
    failed: "Passwort nicht gesetzt",
    doneTitle: "Das Passwort ist gesetzt",
    doneHint: "Bitte melden Sie sich jetzt mit Ihrer E-Mail-Adresse und dem neuen Passwort an.",
    toLogin: "Zur Anmeldung",
    missingToken: "In der Adresse fehlt der Einladungsschlüssel",
  },

  caretaker: {
    title: "Mein Tag",
    asOf: "Stand",
    quickReport: "Schnellmeldung",
    emergency: "Notfall",
    emergencyMark: "Notfall",
    today: "Heute",
    overdue: "Überfällig",
    upcoming: "Demnächst",
    nothingDue: "Für heute steht nichts an.",
    urgentIssues: "Dringende Vorgänge",
    inspections: "Rundgänge, die anstehen",
    access: "Zutritt",
    protected: "Zutritt beschränkt",
    keySystem: "Schließung",
    material: "Material wird gebraucht",
  },
  inspections: {
    title: "Rundgänge",
    list: "Geplant und gelaufen",
    empty: "Für dieses Objekt ist noch kein Rundgang angelegt.",
    plan: "Rundgang planen",
    planned: "Rundgang angelegt",
    titleField: "Bezeichnung",
    template: "Checklistenvorlage",
    noTemplate: "Ohne Vorlage",
    templateHint:
      "Eine Vorlage bestimmt, was an jedem Halt geprüft wird. Ohne sie entsteht ein Rundgang ohne Checkliste.",
    scheduledFor: "Geplant für",
    stops: "Halte",
    start: "Beginnen",
    startAction: "Rundgang starten",
    visited: "Erledigt",
    skipped: "Übersprungen",
    pending: "Offen",
    checked: "geprüft",
    scanned: "Etikett gelesen",
    markVisited: "Halt erledigt",
    visitFailed: "Halt konnte nicht abgeschlossen werden",
    skip: "Überspringen",
    skipReason: "Begründung (Pflicht, steht im Protokoll)",
    skipConfirm: "Halt überspringen",
    scanLabel: "Code vom Etikett",
    scanAction: "Prüfen",
    scanOk: "Etikett passt zu diesem Halt",
    scanFailed: "Code passt nicht",
    resultOk: "In Ordnung",
    resultDeviation: "Abweichung",
    resultOpen: "Offen",
    deviationCreatesIssue: "Aus dieser Abweichung entsteht ein Vorgang.",
    issueCreated: "Vorgang angelegt",
    addNote: "Notiz ergänzen",
    notePlaceholder: "Was ist aufgefallen?",
    finish: "Abschließen",
    finishAction: "Rundgang abschließen",
    finishFailed: "Abschluss nicht möglich",
    finished: "Rundgang abgeschlossen",
    finishedAt: "Abgeschlossen am",
    followUp: "Nächster Rundgang",
    summary: "Zusammenfassung",
    weather: "Witterung",
    weatherHint: "Erklärt später, warum etwas nicht beurteilt werden konnte.",
    result: "Ergebnis",
  },
  zugang: {
    checking: "Zugang wird geprüft",
    invalid: "Dieser Link gilt nicht mehr",
    noToken: "Der Link ist unvollständig. Bitte den vollständigen Link aus der Nachricht öffnen.",
    notLoadable: "Der Inhalt konnte nicht geladen werden",
    untitled: "Ohne Bezeichnung",
    notIncluded: "Nicht im Auftrag",
    scopeNotice:
      "Dieser Zugang zeigt genau den Vorgang, für den er ausgegeben wurde — weitere Objekte oder Aufgaben sind darüber nicht erreichbar.",
    expiryNotice: (minuten: number) =>
      `Der Zugang endet nach etwa ${minuten} Minuten. Danach ist ein neuer Link nötig.`,
  },
  tasks: {
    evidenceMissing: "fehlt",
    complete: "Erledigt melden",
    completed: "Aufgabe erledigt",
    completeFailed: "Abschluss nicht möglich",
    completionNote: "Was wurde gemacht? (freiwillig)",
    comments: "Kommentare",
    commentPlaceholder: "Anmerkung zu dieser Aufgabe",
    addComment: "Kommentar speichern",
    internalOnly: "Nur intern",
    internal: "intern",
    aiWritten: "von einer KI verfasst",
    unknownAuthor: "Unbekannt",
    history: "Verlauf",
    automated: "automatisch",
    title: "Aufgaben",
    mine: "Meine Aufgaben",
    dueToday: "Heute fällig",
    overdue: "Überfällig",
    open: "Offen",
    done: "Erledigt",
    blocked: "Wartet",
    unassigned: "Ohne Zuweisung",
    empty: "Keine Aufgaben in dieser Auswahl",
    emptyHint: "Neue Aufgaben entstehen aus Vorgängen, Begehungen und dem Terminplan.",
    assignee: "Zuständig",
    dueOn: "Fällig",
    startOn: "Beginn",
    trade: "Gewerk",
    dependsOn: "Wartet auf",
    proofRequired: "Nachweis nötig",
    photoRequired: "Foto nötig",
    signatureRequired: "Unterschrift nötig",
    markDone: "Erledigt melden",
    overdueBy: "überfällig seit",
    type: "Art",
    priority: "Dringlichkeit",
    description: "Beschreibung",
    scopeLimits: "Nicht im Auftrag",
    evidenceNeeded: "Nachweise zum Abschluss",
    measurementRequired: "Aufmaß nötig",
    blockedBy: "Wartet auf",
    nextSteps: "Nächster Schritt",
    noNextSteps: "Von hier aus ist kein Schritt möglich.",
    needsEvidence: "Nachweis nötig",
    needsPermission: "Eigenes Recht nötig",
    reasonRequired: "Begründung (wird festgehalten)",
    statusChanged: "Schritt ausgeführt",
    statusFailed: "Schritt nicht möglich",
    followUpCreated: "Neue Aufgabe",
    unblocked: "Freigegeben",
    saved: "Gespeichert",
    days: "Tagen",
  },

  issues: {
    title: "Vorgänge",
    all: "Alle",
    critical: "Kritisch",
    decisions: "Entscheidungen",
    risks: "Risiken",
    observations: "Feststellungen",
    empty: "Keine Vorgänge in dieser Auswahl",
    emptyHint: "Vorgänge entstehen aus Meldungen, Begehungen und Sprachnotizen.",
    /* Entwürfe stehen in **derselben** Übersicht wie die abgeschickten
       Meldungen, aber in einem eigenen Abschnitt darüber: Sie sind noch keine
       Vorgänge, und sie brauchen eine Handlung. Zwei getrennte Listen wären
       zwei Orte, an denen man nachsehen müsste. */
    draftsSection: "Angefangene Meldungen",
    draftsHint: "Noch nicht abgeschickt. Nur für Sie sichtbar.",
    draftUntitled: "Ohne Titel",
    draftSavedAt: "Gesichert",
    draftContinue: "Fortsetzen",
    draftAttachmentsElsewhere: "Anhänge auf einem anderen Gerät",
    cause: "Ursache",
    causeConfirmed: "Bestätigte Ursache",
    causeSuspected: "Vermutete Ursache",
    riskClass: "Risikoklasse",
    measures: "Maßnahmen",
    decisionOpen: "Entscheidung offen",
    decisionNeeded: "Entscheidung nötig",
    evidenceRequired: "Nachweis erforderlich",
    reasonRequired: "Begründung erforderlich",
    changeStatus: "Status ändern",
    history: "Verlauf",
    responsible: "Verantwortlich",
    blocksExecution: "Sperrt die Ausführung",
  },

  costs: {
    runChecks: "Prüfungen ausführen",
    checkDone: "Prüfungen abgeschlossen",
    review: "Rechnung prüfen",
    reviewDone: "Rechnung geprüft",
    approve: "Rechnung freigeben",
    approveDone: "Rechnung freigegeben",
    approved: "Freigegeben",
    actionFailed: "Nicht möglich",
    fourEyesHint:
      "Die Freigabe muss von einer anderen Person kommen als die Prüfung.",
    blockingWarnings: "Offene Warnungen, die eine Freigabe verhindern",
    noteOptional: "Notiz (freiwillig, wird festgehalten)",
    title: "Kosten",
    budget: "Budget",
    committed: "Beauftragt",
    forecast: "Prognose",
    actual: "Ist",
    variance: "Abweichung",
    emptyHint: "Budgets, Angebote, Rechnungen und Nachträge des Projekts.",
    optional: "Eventualpositionen",
    optionalHint: "Zählen nicht zur Angebotssumme — erst nach Freigabe.",
    net: "Netto",
    gross: "Brutto",
    vat: "USt",
    offers: "Angebote",
    invoices: "Rechnungen",
    changeOrders: "Nachträge",
    empty: "Noch keine Kosten erfasst",
    utilization: "Ausschöpfung",
    awaitingApproval: "Freigabe ausstehend",
    secondApproval: "Zweite Freigabe ausstehend",
  },

  offers: {
    title: "Angebot",
    positions: "Positionen",
    position: "Pos.",
    description: "Leistung",
    quantity: "Menge",
    unit: "Einheit",
    unitPrice: "EP netto",
    total: "GP netto",
    inSum: "In Summe",
    notInSum: "Nicht in Summe",
    sum: "Summe",
    checkResult: "Prüfergebnis",
    verified: "Geprüft",
    deviation: "Abweichung",
    plannedQuantity: "Planmenge",
    measuredQuantity: "Bestätigtes Aufmaß",
    supplier: "Anbieter",
    validUntil: "Bindefrist",
    exclusions: "Nicht enthalten",
    clientWork: "Bauseitige Leistungen",
  },

  calendar: {
    title: "Termine",
    today: "Heute",
    week: "Woche",
    month: "Monat",
    upcoming: "Kommend",
    empty: "Keine Termine",
    emptyHint: "Termine entstehen aus Aufgaben, Begehungen und Abstimmungen.",
    allDay: "Ganztägig",
    participants: "Teilnehmer",
    confirmed: "Bestätigt",
    tentative: "Vorbehaltlich",
    location: "Ort",
    conflict: "Überschneidung",
  },

  documents: {
    title: "Dokumente",
    upload: "Hochladen",
    empty: "Keine Dokumente",
    emptyHint: "Angebote, Protokolle, Genehmigungen und Datenblätter liegen hier.",
    type: "Art",
    uploadedOn: "Eingegangen",
    size: "Größe",
    pages: "Seiten",
    ocrPending: "Texterkennung läuft",
    ocrDone: "Text erkannt",
    original: "Original",
    retention: "Aufbewahrung",
  },

  plans: {
    title: "Pläne",
    empty: "Keine Pläne",
    emptyHint: "Grundrisse und Schnitte je Geschoss, mit Markierungen zu Räumen und Vorgängen.",
    floor: "Geschoss",
    scale: "Maßstab",
    markers: "Markierungen",
    addMarker: "Markierung setzen",
    calibrate: "Maßstab festlegen",
    version: "Fassung",
  },

  reports: {
    title: "Berichte",
    empty: "Keine Berichte",
    emptyHint: "Protokolle, Kostenübersichten und Nachweise nach Kapitel 70.",
    generate: "Erzeugen",
    format: "Format",
    parameters: "Angaben",
    generatedOn: "Erzeugt",
    templateVersion: "Vorlage",
    download: "Herunterladen",
  },

  contacts: {
    title: "Kontakte",
    companies: "Firmen",
    people: "Personen",
    empty: "Keine Kontakte",
    emptyHint: "Handwerker, Planer, Behörden und Mieter.",
    role: "Rolle",
    phone: "Telefon",
    email: "E-Mail",
    blocked: "Gesperrt",
    consent: "Einwilligung",
    lastContact: "Letzter Kontakt",
  },

  communication: {
    title: "Kommunikation",
    conversations: "Verläufe",
    empty: "Keine Nachrichten",
    emptyHint: "Ein- und ausgehende Nachrichten je Vorgang und Kontakt.",
    inbound: "Eingang",
    outbound: "Ausgang",
    draft: "Entwurf",
    awaitingApproval: "Freigabe ausstehend",
    reminderStep: "Nachfassstufe",
    commitments: "Zusagen",
    overdueCommitment: "Zusage überschritten",
    aiDisclosure: "Von KI erstellt, vor dem Senden zu prüfen",
    quietHours: "Außerhalb der Kontaktzeiten",
    blockedContact: "Kommunikationssperre",
  },

  queue: {
    title: "Übertragung",
    local: "Örtlich gespeichert",
    sending: "Wird übertragen",
    confirmed: "Bestätigt",
    failed: "Erneuter Versuch",
    blocked: "Wartet",
    partial: "Teilweise übertragen",
    empty: "Alles übertragen",
    pendingCount: "offen",
    retryNow: "Jetzt senden",
    attempts: "Versuche",
    nextAttempt: "Nächster Versuch",
    offline: "Kein Netz — wird nachgeholt",
    online: "Verbunden",
    // Nicht mehr „Nicht übertragene Erfassungen": In der Liste steht seit der
    // Auswertung der Upload-Antworten auch, was zwar angekommen ist, aber einen
    // Hinweis des Servers trägt — etwa eine als Doublette gekennzeichnete Aufnahme.
    detailTitle: "Erfassungen mit offenem Punkt",
    /** Anmerkungen des Servers zu einem Eintrag. */
    notices: "Hinweise",

    /*
     * Rückmeldung, wenn eine Handlung ohne Netz eingereiht wurde.
     *
     * Der Wortlaut ist der Kern: „Gespeichert" allein liest sich wie „beim
     * Server angekommen". Wer das glaubt, ruft niemanden an, wenn die Aufgabe
     * nie ankommt. Deshalb steht ausdrücklich da, wo die Handlung liegt und
     * wann sie weitergeht.
     */
    storedLocally: "Ohne Netz — im Gerät gespeichert",
    storedLocallyHint:
      "Wird übertragen, sobald wieder Verbindung besteht. Bis dahin ist die Änderung nur auf diesem Gerät vorhanden.",

    /** Bezeichnungen der Handlungsarten in der Warteliste. */
    kind: {
      report: "Meldung",
      comment: "Beitrag",
      task: "Neue Aufgabe",
      "task-change": "Aufgabe geändert",
      "issue-change": "Vorgang geändert",
      "stop-visit": "Halt bearbeitet",
      "stop-skip": "Halt übersprungen",
      "stop-scan": "Etikett gelesen",
      "checklist-item": "Prüfpunkt",
    },
  },

  /** Statusbezeichnungen. Farbe allein genügt nach Kapitel 44 nie. */
  status: {
    property: {
      planning: "In Planung",
      active: "Aktiv",
      under_renovation: "In Sanierung",
      archived: "Archiviert",
    },
    room: {
      not_assessed: "Nicht bewertet",
      already_renovated: "Bereits saniert",
      demolition_in_progress: "Rückbau läuft",
      open_ready: "Offen, bereit",
      demolition_required: "Rückbau erforderlich",
      inspection_required: "Prüfung erforderlich",
      renovation_planned: "Sanierung geplant",
      renovation_ordered: "Sanierung beauftragt",
      completed: "Abgeschlossen",
    },
    condition: {
      good: "Gut",
      acceptable: "Annehmbar",
      in_need_of_repair: "Instandsetzung nötig",
      defective: "Mangelhaft",
      critical: "Kritisch",
      unknown: "Unbekannt",
    },
    /** Prioritäten nach Kapitel 12.5. */
    priority: {
      p0: "P0 — akute Gefahr",
      p1: "P1 — sehr dringend",
      p2: "P2 — hoch",
      p3: "P3 — normal",
      p4: "P4 — niedrig",
      p5: "P5 — Beobachtung",
    },
  },

  quantity: {
    estimate: "Schätzung",
    estimateHint: "Aus dem Plan abgeleitet — keine Abrechnungsgrundlage",
    measured: "Aufmaß bestätigt",
  },

  errors: {
    saveFailed: "Konnte nicht gespeichert werden",
    generic: "Etwas ist schiefgelaufen",
    offline: "Keine Verbindung zum Server",
    offlineHint: "Gespeicherte Daten bleiben erhalten und werden später übertragen.",
    /**
     * Für Handlungen, die **nicht** eingereiht werden — Statuswechsel,
     * Freigaben, Beauftragung. Sie hängen an Serverprüfungen (Übergangstabelle,
     * Vier-Augen-Prinzip, Betragsgrenzen), die sich im Gerät nicht vorwegnehmen
     * lassen. Ein „wird später übertragen" wäre hier eine Zusage, die niemand
     * einlöst; der Nutzer muss wissen, dass er es mit Netz wiederholen muss.
     */
    offlineNotQueued:
      "Dieser Schritt braucht eine Verbindung: Er wird am Server geprüft und deshalb nicht zwischengespeichert. Bitte mit Netz wiederholen.",
    unauthorized: "Die Anmeldung ist abgelaufen",
    forbidden: "Für diesen Bereich fehlt die Berechtigung",
    notFound: "Nicht gefunden",
    correlationId: "Vorgangsnummer für den Support",
    reload: "Neu laden",
    /** Netzfehler (Handoff 2b): Zeitpunkt der letzten erfolgreichen Aktualisierung. */
    lastUpdated: (time: string) => `Stand von ${time}`,
    viewSavedState: "Gespeicherten Stand ansehen",
    requestAccess: "Zugang anfragen",
  },

  pwa: {
    updateTitle: "Neue Fassung verfügbar",
    updateBody: "Die Anwendung wird beim Neuladen aktualisiert.",
    updateAction: "Jetzt neu laden",
    updateLater: "Später",
    offlineReady: "Für den Betrieb ohne Netz vorbereitet",
  },
} as const;

export type Translations = typeof de;
